import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { Controller, useForm } from "react-hook-form";
import { getErrorMessage } from "utils/errorHelpers";
import { QuoteTargetSearchTermKind } from "client/api-v2";
import type {
  BillingAccountDto,
  CreateOrderDto,
  QuoteTargetDto,
  QuoteTargetSearchResultDto,
  SalesMotion,
} from "client/api-v2";
import { AlertSeverity, useAlerts } from "components/AlertProvider/AlertProvider";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { useRbacContext } from "components/authorization/RbacProvider";
import { BillingAccountDialog } from "areas/billing-accounts/components/BillingAccountDialog";
import type { BillingAccountOption } from "areas/billing-accounts/components/BillingAccountSelector";
import { BillingAccountSelector } from "areas/billing-accounts/components/BillingAccountSelector";
import { orderToasts } from "areas/orders/components/chrome/orderToasts";
import { orderRoutes } from "areas/orders/order-routes";
import { productPlatformLabels } from "areas/orders/orderConstants";
import { createOrder, searchQuoteTargets } from "areas/orders/services/orderService";
import { describeOrderError, isForbidden } from "areas/orders/utils/orderErrors";
import { subscriptionRoutes } from "areas/subscriptions/subscription-routes";

// Why: mirrors SearchQuoteTargetsQuery.Term's limits, so the box never sends a term the server rejects.
const minimumSearchTermLength = 2;
const maximumSearchTermLength = 200;

const minimumSubscriptionIdFragmentLength = 4;

const searchRefusedMessage = "You do not have permission to search subscriptions.";

const createRefusedMessage = "You do not have permission to create a quote.";

interface CreateQuoteDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (odNumber: string) => void;
  initialTerm?: string;
}

interface SearchFormValues {
  term: string;
}

interface AccountFormValues {
  billingAccount: BillingAccountOption | null;
}

const titleId = "create-quote-dialog-title";

const searchTermKindFields: Record<QuoteTargetSearchTermKind, string> = {
  [QuoteTargetSearchTermKind.CustomerName]: "the customer name",
  [QuoteTargetSearchTermKind.SubscriptionShortId]: "the Subscription ID",
  [QuoteTargetSearchTermKind.SubscriptionId]: "the Subscription ID",
  [QuoteTargetSearchTermKind.LicenceKey]: "the License key",
};

const searchTermKindAdvice: Record<QuoteTargetSearchTermKind, string> = {
  [QuoteTargetSearchTermKind.CustomerName]:
    "A name search reads the billing account name. A subscription with no billing account cannot match a name. " +
    "Try a different name, or search by Subscription ID or License key.",
  [QuoteTargetSearchTermKind.SubscriptionShortId]: "Check the ID, or search by customer name.",
  [QuoteTargetSearchTermKind.SubscriptionId]: "Check the ID, or search by customer name.",
  [QuoteTargetSearchTermKind.LicenceKey]: "Check the key, or search by customer name.",
};

function isTooShortForSubscriptionId(term: string): boolean {
  return term.length < minimumSubscriptionIdFragmentLength && /^[0-9a-f-]+$/i.test(term);
}

function describeFailure(error: unknown, whenUnexplained: string): string {
  const explained = axios.isAxiosError(error) && Boolean(error.response?.data);
  return explained ? describeOrderError(error) : whenUnexplained;
}

function describeSearchFailure(error: unknown): string {
  return isForbidden(error) ? searchRefusedMessage : describeFailure(error, "The search failed. Try again.");
}

function isMissingDefaultRateCard(error: unknown): boolean {
  return (
    axios.isAxiosError(error) && error.response?.status === 400 && /no default rate card/i.test(getErrorMessage(error))
  );
}

function describeCreateFailure(error: unknown, target: QuoteTargetDto, salesMotion: SalesMotion): string {
  if (isForbidden(error)) {
    return createRefusedMessage;
  }
  if (isMissingDefaultRateCard(error)) {
    return (
      `The plan "${
        target.quotedPlanName ?? target.planName
      }" has no default rate card for the ${salesMotion} motion, and this subscription has no active rate card. ` +
      "The quote cannot be priced without one. " +
      "Give the subscription a rate card, or ask an administrator to set the plan's default rate card."
    );
  }
  return describeFailure(error, "The quote was not created. Try again.");
}

function nameTarget(target: QuoteTargetDto): string {
  return target.billingAccountLegalName ?? target.subscriptionName ?? target.serial;
}

function describeTarget(target: QuoteTargetDto): string {
  const subscription = target.subscriptionName ?? target.subscriptionShortId;
  const named = subscription === nameTarget(target) ? [] : [subscription];
  return [...named, target.planName, productPlatformLabels[target.platform]].join(" · ");
}

function summariseMatches(result: QuoteTargetSearchResultDto): string | undefined {
  if (result.targets.length === 0) {
    return undefined;
  }
  const field = searchTermKindFields[result.termKind];
  if (result.termKind !== QuoteTargetSearchTermKind.CustomerName) {
    return `Matched ${field} "${result.term}"`;
  }
  const noun = result.targets.length === 1 ? "subscription" : "subscriptions";
  return `${result.targets.length} ${noun} found for ${field} "${result.term}"`;
}

function describeNoMatch(result: QuoteTargetSearchResultDto): string {
  const opening = `No subscription matches ${searchTermKindFields[result.termKind]} "${result.term}".`;
  if (result.termKind === QuoteTargetSearchTermKind.CustomerName && isTooShortForSubscriptionId(result.term)) {
    return (
      `${opening} A Subscription ID search needs at least ${minimumSubscriptionIdFragmentLength} characters. ` +
      "Enter more of the ID, or search by License key."
    );
  }
  return `${opening} ${searchTermKindAdvice[result.termKind]}`;
}

function SubscriptionLink({ subscriptionId, children }: { subscriptionId: string; children: React.ReactNode }) {
  return (
    // Why: a new tab keeps this dialog and its search results open while the reader checks the subscription.
    <Link
      component={RouterLink}
      to={subscriptionRoutes.detail(subscriptionId).root}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ fontFamily: "monospace" }}
    >
      {children}
    </Link>
  );
}

function QuoteTargetRow({
  target,
  busy,
  onAmend,
  onCreate,
}: {
  target: QuoteTargetDto;
  busy: boolean;
  onAmend: (odNumber: string) => void;
  onCreate: (target: QuoteTargetDto, billingAccountCode?: string) => void;
}) {
  const liveQuoteOdNumber = target.existingQuoteOdNumber;
  const { isAuthorized } = useRbacContext();
  const [creatingAccount, setCreatingAccount] = useState(false);
  const { control, setValue, watch } = useForm<AccountFormValues>({ defaultValues: { billingAccount: null } });
  const chosenAccount = watch("billingAccount");

  const needsAccount = target.billingAccountCode === null;
  const canCreateAccount = isAuthorized(RbacPolicies.BillingAccounts.ReadWrite);
  const hasAPlanToSell = target.quotedPlanId !== null;
  const readyToCreate = hasAPlanToSell && (!needsAccount || chosenAccount !== null);

  const accountCreated = (account: BillingAccountDto) => {
    setCreatingAccount(false);
    setValue("billingAccount", {
      id: account.code,
      code: account.code,
      name: account.commonName ?? account.legalName,
      isCurrentAccount: false,
    });
  };

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", py: 1.75 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1">{nameTarget(target)}</Typography>
          <Typography variant="body2" color="text.secondary">
            License key: <SubscriptionLink subscriptionId={target.subscriptionId}>{target.serial}</SubscriptionLink>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Subscription ID:{" "}
            <SubscriptionLink subscriptionId={target.subscriptionId}>{target.subscriptionId}</SubscriptionLink>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {describeTarget(target)}
          </Typography>
          {target.quotedPlanId !== null && target.quotedPlanId !== target.planId && (
            <Typography variant="body2" color="text.secondary">
              Moving to {target.quotedPlanName}
            </Typography>
          )}
          {target.quotedPlanId === null && (
            <Typography variant="body2" color="error">
              This environment has no {productPlatformLabels[target.platform]} platform plan, so there is nothing to
              quote. Run the migrations.
            </Typography>
          )}
          {liveQuoteOdNumber && (
            <Typography variant="body2" color="text.secondary">
              Active quote {liveQuoteOdNumber}
            </Typography>
          )}
        </Box>
        {liveQuoteOdNumber ? (
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <Chip label="Active quote" color="warning" variant="outlined" size="small" />
            <Button variant="outlined" size="small" disabled={busy} onClick={() => onAmend(liveQuoteOdNumber)}>
              Amend existing
            </Button>
          </Stack>
        ) : (
          <Button
            variant="contained"
            size="small"
            disabled={busy || !readyToCreate}
            onClick={() => onCreate(target, chosenAccount?.code)}
          >
            Create Quote
          </Button>
        )}
      </Stack>
      {needsAccount && !liveQuoteOdNumber && (
        <Stack spacing={1} sx={{ pb: 1.75 }}>
          <Typography variant="body2" color="text.secondary">
            This subscription is not billed to any billing account. Creating the quote puts it on the account you
            choose.
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
            <Box sx={{ flex: 1 }}>
              <BillingAccountSelector name="billingAccount" control={control} required hideResellers disabled={busy} />
            </Box>
            {canCreateAccount && (
              <Button variant="outlined" size="small" disabled={busy} onClick={() => setCreatingAccount(true)}>
                New account
              </Button>
            )}
          </Stack>
          <BillingAccountDialog
            open={creatingAccount}
            onCancel={() => setCreatingAccount(false)}
            onSaved={accountCreated}
          />
        </Stack>
      )}
      <Divider />
    </>
  );
}

function CreateQuoteDialogInner({ onClose, onCreated, initialTerm }: Omit<CreateQuoteDialogProps, "open">) {
  const navigate = useNavigate();
  const { sendNewAlert } = useAlerts();
  const [result, setResult] = useState<QuoteTargetSearchResultDto | undefined>(undefined);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  // Why: `disabled` is not committed before a second click in the same task reaches the handler,
  // so this ref, read and set before the first await, is what stops duplicate orders.
  const creatingRef = useRef(false);
  const [requestError, setRequestError] = useState<string | undefined>(undefined);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SearchFormValues>({ defaultValues: { term: initialTerm ?? "" } });

  const busy = searching || creating;

  const runSearch = useCallback(async ({ term }: SearchFormValues) => {
    setSearching(true);
    setRequestError(undefined);
    try {
      setResult(await searchQuoteTargets(term.trim()));
    } catch (error) {
      setResult(undefined);
      setRequestError(describeSearchFailure(error));
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (initialTerm) {
      void runSearch({ term: initialTerm });
    }
  }, [initialTerm, runSearch]);

  const discardStaleResult = () => {
    setResult(undefined);
    setRequestError(undefined);
  };

  const amendExisting = (odNumber: string) => {
    sendNewAlert(orderToasts.amending(odNumber), AlertSeverity.Info);
    navigate(orderRoutes.detail(odNumber).root);
    onClose();
  };

  const createFor = async (target: QuoteTargetDto, billingAccountCode?: string) => {
    if (creatingRef.current) {
      return;
    }
    creatingRef.current = true;
    setCreating(true);
    setRequestError(undefined);
    const dto: CreateOrderDto = {
      subscriptionId: target.subscriptionId,
      billingAccountCode,
    };
    try {
      const order = await createOrder(dto);
      onCreated(order.odNumber);
    } catch (error) {
      creatingRef.current = false;
      setRequestError(describeCreateFailure(error, target, target.suggestedSalesMotion));
      setCreating(false);
    }
  };

  const errorMessages = [
    ...(errors.term?.message ? [errors.term.message] : []),
    ...(requestError ? [requestError] : []),
  ];
  const matchSummary = result ? summariseMatches(result) : undefined;

  return (
    <Dialog open={true} onClose={() => !busy && onClose()} maxWidth="md" fullWidth aria-labelledby={titleId}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", pr: 3 }}>
        <DialogTitle id={titleId} sx={{ flex: 1 }}>
          Create Quote
        </DialogTitle>
        <Button variant="text" color="secondary" disabled={busy} onClick={onClose}>
          Close
        </Button>
      </Stack>
      <Box
        component="form"
        onSubmit={handleSubmit(runSearch, discardStaleResult)}
        noValidate
        sx={{ px: 3, pb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
          <Controller
            name="term"
            control={control}
            rules={{
              validate: (value) =>
                value.trim().length >= minimumSearchTermLength ||
                `Enter at least ${minimumSearchTermLength} characters to search`,
            }}
            render={({ field: { ref, ...rest }, fieldState }) => (
              <TextField
                {...rest}
                inputRef={ref}
                fullWidth
                label="Customer name, Subscription ID or License key"
                placeholder="e.g. Acme Corp or 0bc6ee8a-4ea3-430c-a7c1-2f9c6e5a1d34"
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: maximumSearchTermLength } }}
                error={fieldState.invalid}
                helperText="Search by customer name to see all of their subscriptions, or enter a Subscription ID or License key"
              />
            )}
          />
          <Button type="submit" variant="outlined" color="secondary" disabled={busy} sx={{ mt: 1 }}>
            Search
          </Button>
        </Stack>
        {errorMessages.length > 0 && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errorMessages.map((message) => (
              <div key={message}>{message}</div>
            ))}
          </Alert>
        )}
      </Box>
      {result && (
        <DialogContent>
          {matchSummary && (
            <Typography variant="body2" color="text.secondary" sx={{ pb: 1 }}>
              {matchSummary}
            </Typography>
          )}
          {result.hasMoreMatches && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Showing the first {result.resultLimit} matches. Narrow the search term to see more.
            </Alert>
          )}
          {result.targets.map((target) => (
            <QuoteTargetRow
              key={target.subscriptionId}
              target={target}
              busy={busy}
              onAmend={amendExisting}
              onCreate={createFor}
            />
          ))}
          {result.targets.length === 0 && <Typography color="text.secondary">{describeNoMatch(result)}</Typography>}
        </DialogContent>
      )}
    </Dialog>
  );
}

export function CreateQuoteDialog({ open, onClose, onCreated, initialTerm }: CreateQuoteDialogProps) {
  if (open) {
    return <CreateQuoteDialogInner onClose={onClose} onCreated={onCreated} initialTerm={initialTerm} />;
  }
  return null;
}
